// admin-activity.js - usage-log surface for admins.
// Reads /api/log/computes (LogController + UsageLogService.GetRecentComputes).

import { getUser } from './auth.js';
import * as events from './events.js';

function token() { return localStorage.getItem('gdbs_token') || ''; }

async function api(method, path) {
  const headers = { 'Content-Type': 'application/json' };
  if (token()) headers['Authorization'] = 'Bearer ' + token();
  const res = await fetch(path, { method, headers });
  if (!res.ok) throw new Error(`${method} ${path}: ${res.status}`);
  return res.json();
}

function escapeHtml(s) {
  if (s === null || s === undefined) return '';
  return String(s).replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;','\'':'&#39;' }[c]));
}

function shortId(id, n = 8) {
  if (!id) return '';
  return id.length > n ? id.slice(0, n) + '...' : id;
}

async function refresh() {
  // /api/log/computes is admin-only. This auto-fires at boot (before login) and
  // on a 15s poll, so guard it: skip until we actually have an admin user, which
  // avoids a guaranteed 403 for everyone else (and for everyone pre-login).
  if (getUser()?.role !== 'admin') return;
  const status = document.getElementById('admin-act-status');
  const body = document.getElementById('admin-act-body');
  const hours = parseInt(document.getElementById('admin-act-hours').value) || 24;
  const module = document.getElementById('admin-act-module').value.trim();
  const max = parseInt(document.getElementById('admin-act-max').value) || 500;
  status.innerHTML = '<i class="fas fa-spinner fa-spin"></i> loading...';
  body.innerHTML = '<tr><td colspan="7" class="admin-empty">Loading...</td></tr>';
  try {
    const params = new URLSearchParams({ hours, max });
    if (module) params.set('module', module);
    const res = await api('GET', '/api/log/computes?' + params);
    const data = res.data || {};
    const rows = data.rows || [];
    status.innerHTML = `<i class="fas fa-check-circle" style="color:var(--accent,#10b981);"></i> ` +
      `${rows.length} compute calls over last ${data.hours}h` +
      (data.module ? ` (module=${escapeHtml(data.module)})` : '');
    if (rows.length === 0) {
      body.innerHTML = '<tr><td colspan="7" class="admin-empty">No matching activity.</td></tr>';
      return;
    }
    body.innerHTML = rows.map(r => {
      const t = r.timestamp || r.Timestamp || '';
      const m = r.module || r.Module || '';
      const a = r.action || r.Action || r.function || '';
      const d = r.durationMs ?? r.DurationMs ?? '';
      const u = r.userId || r.UserId || '';
      const tier = r.tier || r.Tier || '';
      const s = r.sessionId || r.SessionId || '';
      return `<tr>
        <td><code>${escapeHtml(t)}</code></td>
        <td>${escapeHtml(m)}</td>
        <td>${escapeHtml(a)}</td>
        <td style="text-align:right;font-variant-numeric:tabular-nums;">${escapeHtml(d)}</td>
        <td><code title="${escapeHtml(u)}">${escapeHtml(shortId(u))}</code></td>
        <td>${escapeHtml(tier)}</td>
        <td><code title="${escapeHtml(s)}">${escapeHtml(shortId(s))}</code></td>
      </tr>`;
    }).join('');
  } catch (e) {
    status.innerHTML = `<i class="fas fa-times-circle" style="color:var(--accent-red,oklch(60% 0.20 25));"></i> ${escapeHtml(e.message)}`;
    body.innerHTML = `<tr><td colspan="7" class="admin-empty">${escapeHtml(e.message)}</td></tr>`;
  }
}

// Refresh only when the panel is actually visible (offsetParent === null when its
// view is hidden, so we never refresh in the background).
function refreshIfVisible() {
  const el = document.getElementById('admin-act-body');
  if (el && el.offsetParent !== null) refresh();
}

export function initAdminActivity() {
  const btn = document.getElementById('btn-act-refresh');
  if (btn) btn.addEventListener('click', refresh);
  // Initial load.
  refresh();
  // Event-driven: re-fetch when the server pushes an 'activity' event (a new
  // compute call was logged) instead of a tight 15s poll.
  try { events.on('activity', refreshIfVisible); } catch (_) {}
  // Slow safety timer: if the stream is unavailable, this keeps the panel
  // eventually-fresh without the old 15s churn. 60s, visible-only.
  if (window.__adminActTimer) clearInterval(window.__adminActTimer);
  window.__adminActTimer = setInterval(refreshIfVisible, 60000);
}
